import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ButtonV2,
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  StatusChipV4,
  TableComponentV2,
  SearchBoxComponent,
} from '@labrav/react-components'
import {
  type CloudAccountChangeParams,
  useCloudAccounts,
  getSelectedAccountIdWithCloud,
  extractAccountIdFromCombinedId,
  actionTypeWrapper,
  CloudType,
  setSelectedAccount,
  SearchResultsCount,
  startLoading,
  stopLoading,
  LoadingTypes,
  isLoading,
  useCurrentCloudProvider,
  useAccountId,
  searchDropDownOptionMapping,
  searchDropDownOptions,
} from '@labrav/common'
import { useFlagValue } from '@labrav/flags'
import { isEmpty } from 'lodash'
import clsx from 'clsx'
import { useNavigate, useParams } from 'react-router-dom'
import { ShowExpirationDateWithIcon } from '../../../common/components/ShowExpirationDateWithIcon/ShowExpirationDateWithIcon'
import { ResponsiveColumn } from '../../../common/components/ResponsiveColumn/ResponsiveColumn'
import { EmptyState } from '../../../common/components/EmptyState/EmptyState'
import Disable from '../../../common/images/Disable.png'
import { useCommonStyles } from '../../../common/styles/common.styles'
import { useDispatch, useSelector } from '../../../store'
import {
  clearReceivedResaleAuthorizationData,
  getReceivedResaleAuthorizationsForCP,
  syncReceivedResaleAuthorizations,
} from '../../../modules/receivedResellerAuthorization/action'
import { setSelectedProductOnListing } from '../../../modules/productsListing/actionCreators'
import { clearProductsListing } from '../../../modules/productsListing/actionCreators'
import { setCloudMarketplaceProductListing } from '../../../modules/productListingFilter/action'
import { getStatusAndLabelForResale } from '../../../common/utils/getStatusAndLabelForResale'
import { ResellerAgreementStatus } from '../../../types/flyout'
import { useScreenSize } from '@labrav/common'
import { useStyles } from './ReceivedResellerAuthorizations.styles'
import {
  mappingResellerForLabraUI,
  customCloudSelectorOptions,
} from './constants'
import {
  getReceivedResaleMenuOptions,
  getChannelOfferEditPath,
} from './helpers'
import { CloudAndCreateChannelOffer } from './CloudAndCreateChannelOffer'
import type { ReceivedResellerAuthorizationsProps } from '../types'
import { ChannelOfferCreationModal } from '../../ChannelOffers/ChannelOfferCreationModal'

export type { ReceivedResellerAuthorizationsProps }

export const ReceivedResellerAuthorizations: React.FC<
  ReceivedResellerAuthorizationsProps
> = ({ externalBaseURL, crmOpportunityId }) => {
  const { classes } = useStyles()
  const { classes: commonClasses } = useCommonStyles()
  const { cloud, accountId: accountIdFromParams } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const accountIdFromHook = useAccountId({ product: 'flyout' }) || ''
  const accountId = accountIdFromParams || accountIdFromHook
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const pageSizeObj = useScreenSize()
  const { screenWidth } = useScreenSize()
  const selectedCloudTypeProductListing = useCurrentCloudProvider()
  const [searchNow, setSearchNow] = useState(false)
  const isSearchFeautureCppo =
    useFlagValue('searchFeautureCppo')?.value ?? false

  const baseUrl = useMemo(() => {
    if (externalBaseURL) return externalBaseURL
    if (cloud && accountId) {
      return `/flyout/${cloud}/${accountId}`
    }
    return '/flyout'
  }, [externalBaseURL, cloud, accountId])

  const baseMarketplaceCloudAccounts = useCloudAccounts('flyout')

  const marketplaceCloudAccounts = useMemo(
    () =>
      baseMarketplaceCloudAccounts.map(account => ({
        ...account,
        disabled: account.provider !== 'AWS',
      })),
    [baseMarketplaceCloudAccounts]
  )

  const selectedAccountIdWithCloud = getSelectedAccountIdWithCloud(
    accountId,
    'flyout',
    cloud || selectedCloudTypeProductListing
  )

  const handleCloudAccountChange = useCallback(
    async (params: CloudAccountChangeParams) => {
      const extractedAccountId = extractAccountIdFromCombinedId(
        params.accountId
      )
      const { cloud: newCloud } = params

      const cloudPath = newCloud.toLowerCase()
      const pathname = `/flyout/${cloudPath}/${extractedAccountId}/reseller-authorizations/received`
      navigate(pathname, { replace: true })

      await dispatch(startLoading(LoadingTypes.GENERAL))

      await dispatch(
        setSelectedAccount(extractedAccountId, 'flyout', newCloud as CloudType)
      )

      await dispatch(
        actionTypeWrapper(
          selectedCloudTypeProductListing,
          setSelectedProductOnListing(undefined)
        )
      )

      await dispatch(actionTypeWrapper(newCloud, clearProductsListing()))
      await dispatch(setCloudMarketplaceProductListing(newCloud))

      await dispatch(clearReceivedResaleAuthorizationData())
      await dispatch(
        getReceivedResaleAuthorizationsForCP({
          partnerId: extractedAccountId,
          pageSize: pageSizeObj.pageSize || 15,
        })
      )

      await dispatch(stopLoading(LoadingTypes.GENERAL))

      setInputValue('')
      setShowCrossMark(false)
      setSearchNow(false)

      navigate(pathname)
    },
    [dispatch, navigate, selectedCloudTypeProductListing, pageSizeObj.pageSize]
  )

  const columnMaxWidth = useMemo(
    () => `${(screenWidth * 18) / 100}px`,
    [screenWidth]
  )

  const allAccounts = useSelector(
    state =>
      (state.accountSelection?.availableAccounts || []) as {
        id?: string
        cloud?: string
        product?: string
      }[]
  )

  useEffect(() => {
    if (
      !externalBaseURL &&
      cloud &&
      cloud.toUpperCase() !== 'AWS' &&
      accountId
    ) {
      const flyoutAccounts = allAccounts.filter(
        acc => acc.product?.toLowerCase() === 'flyout'
      )

      let awsAccountId = flyoutAccounts.find(
        acc => acc.id === accountId && acc.cloud === 'AWS'
      )?.id

      if (!awsAccountId) {
        awsAccountId = flyoutAccounts.find(acc => acc.cloud === 'AWS')?.id
      }

      if (awsAccountId) {
        navigate(
          `/flyout/aws/${awsAccountId}/reseller-authorizations/received`,
          { replace: true }
        )
      }
    }
  }, [cloud, accountId, externalBaseURL, navigate, allAccounts])

  const getResaleProps = {
    partnerId: accountId,
    pageSize: pageSizeObj.pageSize || 15,
  }
  useEffect(() => {
    if (!searchNow) {
      dispatch(clearReceivedResaleAuthorizationData())
      dispatch(getReceivedResaleAuthorizationsForCP(getResaleProps))
    }
  }, [getResaleProps.partnerId, searchNow, accountId])

  const { resellerAgreements, pageNumber, count } = useSelector(
    state => state.receivedResellerAuthorization
  )
  const [showCrossMark, setShowCrossMark] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedOption, setSelectedOption] = useState(
    searchDropDownOptions.resaleAuthorizationOptions[0]
  )
  const loading = useSelector(state =>
    isLoading(state.loading)(LoadingTypes.FETCHING_RECEIVED_RESALE_AUTHS)
  )
  const isLoadingSync = useSelector(state =>
    isLoading(state.loading)(LoadingTypes.SYNC_RECEIVED_RESALE_AUTHS_FROM_CLOUD)
  )

  const [shouldDisplayWaitTime, setDisplayWaitTime] = useState<boolean>(false)

  const [open, setOpen] = useState<string>('')

  const selectedActiveReceivedResellerAuthorization = useSelector(
    state =>
      state?.receivedResellerAuthorization
        ?.selectedActiveReceivedResellerAuthorization
  )

  const handleNext = () => {
    navigate(
      getChannelOfferEditPath({
        externalBaseURL,
        cloud,
        accountId,
        authorizationId: selectedActiveReceivedResellerAuthorization,
      })
    )
  }

  const handleSelectOption =
    (agreementId: string) => async (option: ColumnMenuOption) => {
      if (option.route) {
        navigate(option?.route)
      }
    }

  const fetchMoreData = useCallback(() => {
    if (resellerAgreements) {
      {
        dispatch(
          getReceivedResaleAuthorizationsForCP({
            partnerId: accountId,
            pageSize: pageSizeObj.pageSize,
            pageNumber: pageNumber + 1,
            crmOpportunityId,
            ...(!isEmpty(inputValue) && !isEmpty(selectedOption)
              ? {
                  input: inputValue,
                  selectedInput:
                    searchDropDownOptionMapping
                      .receivedResaleAuthorizationOptions[
                      selectedOption as keyof typeof searchDropDownOptionMapping.receivedResaleAuthorizationOptions
                    ],
                  sectionName: 'resale-authorizations',
                }
              : {}),
          })
        )
      }
    }
  }, [accountId, pageNumber, resellerAgreements, crmOpportunityId])

  const getResaleListObjectsForLabraUI = () => {
    return [
      ...resellerAgreements.map(ra => {
        const statusDetails = getStatusAndLabelForResale(
          ra?.status || ResellerAgreementStatus.IN_PROGRESS
        )
        return {
          id: ra?.id,
          isvName: (
            <ResponsiveColumn
              linkText={ra?.isvName || '-'}
              maxWidth={columnMaxWidth}
            />
          ),
          authorizationName: (
            <ResponsiveColumn
              linkText={ra.agreementName}
              maxWidth={columnMaxWidth}
            />
          ),
          productName: (
            <ResponsiveColumn
              linkText={ra.cloudProductName || ''}
              maxWidth={columnMaxWidth}
            />
          ),
          endDate: !isEmpty(ra.agreementExpirationDate) ? (
            <ShowExpirationDateWithIcon
              date={ra.agreementExpirationDate || ''}
              daysToCompare={5}
              daysLessLimit={15}
              expirePrefix="Ends in"
            />
          ) : (
            '-'
          ),
          status: isEmpty(ra?.status) ? (
            '-'
          ) : (
            <StatusChipV4 {...statusDetails} />
          ),
          actions: (
            <div
              className={commonClasses.actionColumn}
              data-prevent-row-click
              data-testid={'actions'}
            >
              <ColumnActionWithMenuV2
                handleSelectOption={handleSelectOption(ra.id)}
                menuOptions={getReceivedResaleMenuOptions({
                  agreementId: ra.id,
                  baseUrl,
                  externalBaseURL,
                  cloud,
                  accountId,
                })}
              />
            </div>
          ) as never,
        }
      }),
    ]
  }

  const handleSyncButtonClick = () => {
    dispatch(syncReceivedResaleAuthorizations(accountId))
    setDisplayWaitTime(true)
  }

  const handleSearchClick = async () => {
    if (inputValue.trim() !== '') {
      setSearchNow(true)
      setShowCrossMark(true)
      await dispatch(clearReceivedResaleAuthorizationData())
      dispatch(
        getReceivedResaleAuthorizationsForCP({
          partnerId: accountId,
          pageSize: pageSizeObj.pageSize,
          pageNumber: 1,
          crmOpportunityId,
          input: inputValue,
          selectedInput:
            searchDropDownOptionMapping.receivedResaleAuthorizationOptions[
              selectedOption as keyof typeof searchDropDownOptionMapping.receivedResaleAuthorizationOptions
            ],
          sectionName: 'resale-authorizations',
        })
      )
    }
  }

  const handleClickRow = (selectedId: string) => {
    const selectedItem = resellerAgreements?.find(
      item => item?.id === selectedId
    )
    if (selectedItem) {
      const options = getReceivedResaleMenuOptions({
        agreementId: selectedItem.id,
        baseUrl,
        externalBaseURL,
        cloud,
        accountId,
      })
      navigate(options[0].route)
    }
  }

  const cloudAndCreateProps = {
    externalBaseURL,
    customCloudSelectorOptions,
    setInputValue,
    setShowCrossMark,
    setSearchNow,
    marketplaceCloudAccounts,
    selectedAccountIdWithCloud,
    handleCloudAccountChange,
    onCreateChannelOffer: () => setOpen('create'),
  }

  return (
    <>
      {isEmpty(resellerAgreements) && !searchNow ? (
        <div
          className={classes.emptyResaleState}
          data-testid="empty-received-resale-auths-card"
        >
          <EmptyState
            imgSrc={Disable}
            header="Get your first Reseller authorization"
            content={
              <div className={classes.emptyResaleStateContent}>
                <p>
                  Grow your revenue by getting your Reseller authorizations to
                  enjoy effortless channel offer management and more benefits.
                </p>
              </div>
            }
            action={
              <div>
                <ButtonV2
                  onClick={handleSyncButtonClick}
                  data-testid="button-sync-resale-authorizations"
                  loading={isLoadingSync}
                  disabled={!isLoadingSync && shouldDisplayWaitTime}
                >
                  Sync authorizations from AWS
                </ButtonV2>
                {!isLoadingSync && shouldDisplayWaitTime && (
                  <p className={classes.waitText}>
                    Please wait for 5 mins and refresh the page as we sync
                    authorizations meanwhile in background
                  </p>
                )}
              </div>
            }
          />
        </div>
      ) : (
        <div
          className={classes.receivedResaleAuths}
          data-testid={'received-resale-auth-list-cp'}
        >
          {isSearchFeautureCppo && (
            <div
              className={clsx(
                classes.upperContainter,
                classes.justifySpaceBetween
              )}
            >
              {selectedCloudTypeProductListing === 'AWS' && !externalBaseURL ? (
                <SearchBoxComponent
                  options={
                    searchDropDownOptions.receivedResaleAuthorizationOptions
                  }
                  handleSearchClick={handleSearchClick}
                  loading={loading}
                  searchNow={searchNow}
                  setSearchNow={setSearchNow}
                  showCrossMark={showCrossMark}
                  setShowCrossMark={setShowCrossMark}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  selectedOption={selectedOption}
                  setSelectedOption={setSelectedOption}
                />
              ) : (
                <></>
              )}
              <CloudAndCreateChannelOffer {...cloudAndCreateProps} />
            </div>
          )}
          {!isSearchFeautureCppo && (
            <CloudAndCreateChannelOffer {...cloudAndCreateProps} />
          )}
          {searchNow && (
            <SearchResultsCount totalCount={count} loading={loading} />
          )}
          <TableComponentV2
            isLoading={loading}
            keyName={'migrationDetails.products'}
            objects={getResaleListObjectsForLabraUI()}
            mapping={mappingResellerForLabraUI}
            count={count || 0}
            fetchMoreData={fetchMoreData}
            selectedItem={[]}
            onChangeCheckbox={() => void 0}
            showCheckbox={false}
            breakWorkHeader={false}
            tableHeight={'calc(100vh - 242px)'}
            onChangeRow={handleClickRow}
            customColumns={['actions']}
            customCellProps={[
              { style: { width: columnMaxWidth } },
              { style: { width: columnMaxWidth } },
              { style: { width: columnMaxWidth } },
            ]}
            stickyColumns={['actions']}
          />
          <ChannelOfferCreationModal
            open={open}
            setOpen={setOpen}
            handleNext={handleNext}
          />
        </div>
      )}
    </>
  )
}
